import { useState, useMemo } from "react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Collapsible, CollapsibleContent, CollapsibleTrigger,
} from "../components/ui/collapsible";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ChevronDown, ChevronRight, Download, RotateCcw, BarChart3, Printer, FileText, UserX } from "lucide-react";
import { usePasses } from "../context/PassesContext";
import { useTalmidim } from "../context/TalmidimContext";
import { useAbsences } from "../context/AbsencesContext";
import { useSettings } from "../context/SettingsContext";
import { useToast } from "../hooks/use-toast";
import { formatHebrewDateShort, format12Hour } from "../lib/hebrewDate";
import { getReasonCategory, CATEGORY_JUSTIFIED, CATEGORY_UNJUSTIFIED, CATEGORY_LABEL_HE } from "../lib/reasons";

function firstDayOfMonthISO() {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
}
function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

export default function Reports() {
  const { passes } = usePasses();
  const { absences } = useAbsences();
  const { klasn } = useTalmidim();
  const { settings } = useSettings();
  const { toast } = useToast();

  const [type, setType] = useState("passes"); // "passes" | "absences"
  const [from, setFrom] = useState(firstDayOfMonthISO());
  const [to, setTo] = useState(todayISO());
  const [klasFilter, setKlasFilter] = useState("all");
  const [nameSearch, setNameSearch] = useState("");
  const [expanded, setExpanded] = useState({});

  const source = type === "absences" ? absences : passes;
  const reasonList = settings?.commonReasons;

  const catFor = (pass) => {
    if (pass && typeof pass === "object" && pass.reasonCategory) return pass.reasonCategory;
    const text = typeof pass === "string" ? pass : pass?.reason;
    return getReasonCategory(text, reasonList);
  };

  const inRange = useMemo(() => {
    return source.filter((p) => {
      if (from && p.date < from) return false;
      if (to && p.date > to) return false;
      if (klasFilter !== "all" && String(p.klasId) !== klasFilter) return false;
      if (nameSearch && !p.talmidName.includes(nameSearch)) return false;
      return true;
    });
  }, [source, from, to, klasFilter, nameSearch]);

  // Group by student
  const grouped = useMemo(() => {
    const map = new Map();
    for (const p of inRange) {
      const key = `${p.talmidId}|${p.talmidName}`;
      if (!map.has(key)) {
        map.set(key, {
          talmidId: p.talmidId,
          talmidName: p.talmidName,
          klasName: p.klasName,
          klasId: p.klasId,
          total: 0,
          justifiedCount: 0,
          unjustifiedCount: 0,
          uncategorizedCount: 0,
          reasons: new Map(),
          items: [],
        });
      }
      const entry = map.get(key);
      entry.total += 1;
      entry.items.push(p);
      if (p.reason) {
        entry.reasons.set(p.reason, (entry.reasons.get(p.reason) || 0) + 1);
        const c = catFor(p);
        if (c === CATEGORY_JUSTIFIED) entry.justifiedCount += 1;
        else if (c === CATEGORY_UNJUSTIFIED) entry.unjustifiedCount += 1;
        else entry.uncategorizedCount += 1;
      } else {
        entry.uncategorizedCount += 1;
      }
      // Keep last klas
      entry.klasName = p.klasName;
      entry.klasId = p.klasId;
    }
    const arr = Array.from(map.values()).map((e) => ({
      ...e,
      reasons: Array.from(e.reasons.entries())
        .map(([reason, count]) => ({ reason, count, category: catFor(reason) }))
        .sort((a, b) => b.count - a.count),
      items: e.items.sort((a, b) => {
        const at = (a.time || "");
        const bt = (b.time || "");
        return (b.date + bt).localeCompare(a.date + at);
      }),
    }));
    arr.sort((a, b) => b.total - a.total || a.talmidName.localeCompare(b.talmidName));
    return arr;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inRange, type, reasonList]);

  // Global totals for justified/unjustified
  const globalJustified = grouped.reduce((s, g) => s + g.justifiedCount, 0);
  const globalUnjustified = grouped.reduce((s, g) => s + g.unjustifiedCount, 0);

  const totalItems = inRange.length;
  const uniqueStudents = grouped.length;
  const avgPerStudent = uniqueStudents > 0 ? (totalItems / uniqueStudents).toFixed(1) : "0.0";
  const isPasses = type === "passes";
  const labels = isPasses
    ? { title: "Late Passes", subtitle: "Per-student breakdown of late passes over a period", total: "Total Passes", singular: "pass", plural: "passes", exportLabel: "late-pass-report" }
    : { title: "Absences", subtitle: "Per-student breakdown of absences over a period", total: "Total Absences", singular: "absence", plural: "absences", exportLabel: "absence-report" };

  const resetFilters = () => {
    setFrom(firstDayOfMonthISO());
    setTo(todayISO());
    setKlasFilter("all");
    setNameSearch("");
  };

  const setPreset = (preset) => {
    const today = new Date();
    if (preset === "today") {
      const t = todayISO();
      setFrom(t); setTo(t);
    } else if (preset === "week") {
      const d = new Date(today);
      d.setDate(d.getDate() - 6);
      setFrom(d.toISOString().slice(0, 10)); setTo(todayISO());
    } else if (preset === "month") {
      setFrom(firstDayOfMonthISO()); setTo(todayISO());
    } else if (preset === "30d") {
      const d = new Date(today);
      d.setDate(d.getDate() - 29);
      setFrom(d.toISOString().slice(0, 10)); setTo(todayISO());
    } else if (preset === "all") {
      setFrom(""); setTo("");
    }
  };

  const exportCsv = () => {
    if (grouped.length === 0) {
      toast({ title: "Nothing to export", variant: "destructive" });
      return;
    }
    const header = isPasses
      ? ["Student", "Class", "Total Passes", "בארעכטיגט", "נישט בארעכטיגט", "Reasons Breakdown", "Dates"]
      : ["Student", "Class", "Total Absences", "Dates"];
    const rows = grouped.map((g) => {
      const dates = g.items
        .map((p) => isPasses ? `${formatHebrewDateShort(p.date)} ${format12Hour(p.time)}` : formatHebrewDateShort(p.date))
        .join("; ");
      return isPasses
        ? [g.talmidName, g.klasName, g.total, g.justifiedCount, g.unjustifiedCount, g.reasons.map((r) => `${r.reason} (${r.count})`).join("; "), dates]
        : [g.talmidName, g.klasName, g.total, dates];
    });
    const csv = [header, ...rows]
      .map((r) => r.map((c) => `"${(c ?? "").toString().replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const label = from && to ? `${from}_to_${to}` : "all-time";
    a.download = `${labels.exportLabel}-${label}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast({ title: "Exported", description: `${grouped.length} students, ${totalItems} ${labels.plural}` });
  };

  const toggleExpand = (id) => setExpanded((s) => ({ ...s, [id]: !s[id] }));

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-serif-heb text-3xl font-bold text-slate-900">Reports</h1>
          <p className="text-slate-500 text-sm mt-1">{labels.subtitle}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={resetFilters} data-testid="reset-filters-btn">
            <RotateCcw className="w-4 h-4 mr-2" />Reset
          </Button>
          <Button variant="outline" onClick={() => window.print()} data-testid="print-report-btn">
            <Printer className="w-4 h-4 mr-2" />Print
          </Button>
          <Button onClick={exportCsv} className="gradient-blue-mild text-white" data-testid="export-report-btn">
            <Download className="w-4 h-4 mr-2" />Export CSV
          </Button>
        </div>
      </div>

      {/* Type tabs */}
      <Tabs value={type} onValueChange={setType}>
        <TabsList className="grid grid-cols-2 w-full max-w-md">
          <TabsTrigger value="passes" data-testid="tab-passes">
            <FileText className="w-4 h-4 mr-2" />Late Passes
          </TabsTrigger>
          <TabsTrigger value="absences" data-testid="tab-absences">
            <UserX className="w-4 h-4 mr-2" />Absences <span dir="rtl" className="ms-1 text-slate-500">(חסרים)</span>
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="p-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">From</label>
              <Input
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                className="font-en"
                data-testid="from-date-input"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">To</label>
              <Input
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                className="font-en"
                data-testid="to-date-input"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">Class</label>
              <Select value={klasFilter} onValueChange={setKlasFilter}>
                <SelectTrigger data-testid="class-filter-select">
                  <SelectValue placeholder="All Classes" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Classes</SelectItem>
                  {klasn.map((k) => (
                    <SelectItem key={k.id} value={String(k.id)}>
                      <span dir="rtl">{k.name}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">Student name</label>
              <Input
                value={nameSearch}
                onChange={(e) => setNameSearch(e.target.value)}
                placeholder="Filter by name"
                dir="rtl"
                data-testid="name-search-input"
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-2 pt-1">
            <span className="text-xs text-slate-500 self-center">Quick range:</span>
            {[
              { k: "today", label: "Today" },
              { k: "week", label: "Last 7 days" },
              { k: "30d", label: "Last 30 days" },
              { k: "month", label: "This month" },
              { k: "all", label: "All time" },
            ].map((p) => (
              <Button
                key={p.k}
                size="sm"
                variant="ghost"
                className="h-7 text-xs"
                onClick={() => setPreset(p.k)}
                data-testid={`preset-${p.k}`}
              >
                {p.label}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <SummaryCard label={labels.total} value={totalItems} testId="stat-total" />
        <SummaryCard label="Unique Students" value={uniqueStudents} testId="stat-unique" />
        <SummaryCard label="Avg per Student" value={avgPerStudent} testId="stat-avg" />
        <SummaryCard
          label={<span dir="rtl">בארעכטיגט</span>}
          value={globalJustified}
          testId="stat-justified"
          color="emerald"
        />
        <SummaryCard
          label={<span dir="rtl">נישט בארעכטיגט</span>}
          value={globalUnjustified}
          testId="stat-unjustified"
          color="red"
        />
      </div>

      {/* Per-student list */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {grouped.length === 0 && (
            <div className="p-16 text-center text-slate-400">
              <BarChart3 className="w-10 h-10 mx-auto mb-3 opacity-40" />
              No {labels.plural} in this period
            </div>
          )}
          {grouped.map((g, idx) => {
            const key = `${g.talmidId}-${g.talmidName}`;
            const open = !!expanded[key];
            return (
              <Collapsible
                key={key}
                open={open}
                onOpenChange={() => toggleExpand(key)}
              >
                <CollapsibleTrigger
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 border-b border-slate-100 text-left"
                  data-testid={`student-row-${idx}`}
                >
                  {open ? (
                    <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
                  )}
                  <div className={`w-8 h-8 rounded-full ${isPasses ? "bg-blue-50 text-blue-700" : "bg-red-50 text-red-700"} flex items-center justify-center text-xs font-semibold flex-shrink-0`}>
                    {idx + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-slate-800 truncate" dir="rtl">
                      {g.talmidName}
                    </div>
                    <div className="text-xs text-slate-500" dir="rtl">
                      {g.klasName}
                    </div>
                  </div>
                  {g.reasons.length > 0 && (
                    <div className="hidden md:flex gap-1 flex-wrap justify-end max-w-md">
                      {g.reasons.slice(0, 3).map((r) => (
                        <Badge
                          key={r.reason}
                          variant="secondary"
                          className={`font-normal ${
                            r.category === CATEGORY_JUSTIFIED
                              ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-50"
                              : r.category === CATEGORY_UNJUSTIFIED
                                ? "bg-red-50 text-red-700 hover:bg-red-50"
                                : ""
                          }`}
                        >
                          <span dir="rtl">{r.reason}</span>
                          <span className="ms-1 opacity-60">×{r.count}</span>
                        </Badge>
                      ))}
                      {g.reasons.length > 3 && (
                        <Badge variant="outline" className="font-normal">
                          +{g.reasons.length - 3} more
                        </Badge>
                      )}
                    </div>
                  )}
                  <div className="flex items-center gap-3 flex-shrink-0 ml-2">
                    {isPasses && (g.justifiedCount > 0 || g.unjustifiedCount > 0) && (
                      <div className="hidden sm:flex flex-col items-end text-[10px] leading-tight">
                        {g.justifiedCount > 0 && (
                          <div className="flex items-center gap-1 text-emerald-700" title="בארעכטיגט">
                            <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                            <span className="font-semibold text-sm">{g.justifiedCount}</span>
                            <span dir="rtl" className="opacity-60">בארעכטיגט</span>
                          </div>
                        )}
                        {g.unjustifiedCount > 0 && (
                          <div className="flex items-center gap-1 text-red-700 mt-0.5" title="נישט בארעכטיגט">
                            <div className="w-1.5 h-1.5 rounded-full bg-red-500" />
                            <span className="font-semibold text-sm">{g.unjustifiedCount}</span>
                            <span dir="rtl" className="opacity-60">נישט בארעכטיגט</span>
                          </div>
                        )}
                      </div>
                    )}
                    <div>
                      <div className="text-2xl font-bold text-slate-800 leading-none text-right">
                        {g.total}
                      </div>
                      <div className="text-[10px] text-slate-400 uppercase tracking-wide text-right">
                        {labels.plural}
                      </div>
                    </div>
                  </div>
                </CollapsibleTrigger>

                <CollapsibleContent className="bg-slate-50 border-b border-slate-100">
                  <div className="p-4 space-y-3">
                    {g.reasons.length > 0 && (
                      <div>
                        <div className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">
                          Reasons breakdown
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {g.reasons.map((r) => (
                            <div
                              key={r.reason}
                              className="bg-white border border-slate-200 rounded-full px-3 py-1 text-sm flex items-center gap-2"
                              dir="rtl"
                            >
                              <span>{r.reason}</span>
                              <Badge variant="secondary" className="ms-1">
                                {r.count}
                              </Badge>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div>
                      <div className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">
                        All {labels.plural} ({g.items.length})
                      </div>
                      <div className="overflow-x-auto bg-white border border-slate-200 rounded-lg">
                        <table className="w-full text-sm">
                          <thead className="bg-slate-100 text-xs uppercase text-slate-500">
                            <tr>
                              {isPasses && <th className="px-3 py-2 text-left">Pass #</th>}
                              <th className="px-3 py-2 text-left">Date</th>
                              {isPasses && <th className="px-3 py-2 text-left">Time</th>}
                              <th className="px-3 py-2 text-left">Reason</th>
                              {isPasses && <th className="px-3 py-2 text-left">Category</th>}
                              {!isPasses && <th className="px-3 py-2 text-left">Marked by</th>}
                            </tr>
                          </thead>
                          <tbody>
                            {g.items.map((p) => {
                              const cat = isPasses ? catFor(p) : null;
                              return (
                              <tr
                                key={p.id}
                                className="border-t border-slate-100"
                              >
                                {isPasses && (
                                  <td className="px-3 py-2 font-mono text-xs text-slate-500">
                                    {p.id}
                                  </td>
                                )}
                                <td className="px-3 py-2 text-slate-700" dir="rtl">
                                  {formatHebrewDateShort(p.date)}
                                </td>
                                {isPasses && (
                                  <td className="px-3 py-2 font-en text-slate-600">
                                    {format12Hour(p.time)}
                                  </td>
                                )}
                                <td className="px-3 py-2 text-slate-700" dir="rtl">
                                  {p.reason || <span className="text-slate-400">—</span>}
                                </td>
                                {isPasses && (
                                  <td className="px-3 py-2" dir="rtl">
                                    {cat ? (
                                      <Badge
                                        className={
                                          cat === CATEGORY_JUSTIFIED
                                            ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-100"
                                            : "bg-red-100 text-red-800 hover:bg-red-100"
                                        }
                                      >
                                        {CATEGORY_LABEL_HE[cat]}
                                      </Badge>
                                    ) : (
                                      <span className="text-slate-400 text-xs">—</span>
                                    )}
                                  </td>
                                )}
                                {!isPasses && (
                                  <td className="px-3 py-2 text-slate-600" dir="rtl">
                                    {p.markedBy || "—"}
                                  </td>
                                )}
                              </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </CollapsibleContent>
              </Collapsible>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
}

function SummaryCard({ label, value, testId, color = "slate" }) {
  const colors = {
    slate: "text-slate-900",
    emerald: "text-emerald-700",
    red: "text-red-700",
  };
  return (
    <Card className="border-slate-200">
      <CardContent className="p-5">
        <div className="text-xs uppercase tracking-wide text-slate-500">
          {label}
        </div>
        <div
          className={`text-3xl font-bold mt-1 ${colors[color] || colors.slate}`}
          data-testid={testId}
        >
          {value}
        </div>
      </CardContent>
    </Card>
  );
}
